import {useMemo} from 'react';
import type {ResultGetGroupedSupportCompanies} from '../../../core/repositories/companyRepository';

type SelectOption = {
  label: string;
  value: string;
};

export type AutocompleteOption = {
  id: number;
  value: string;
};

/**
 * 企業グループをフラット化し、重複を除去してセレクト/オートコンプリート用のデータ形式に変換
 *
 * @param companiesData - useGetGroupedSupportCompaniesから取得したデータ
 * @param options - フォーマットオプション
 * @returns フォーマットに応じた企業リスト
 *
 * @example
 * // Select用
 * const companyList = useCompanyList(companiesData, { format: 'select' });
 * // => [{label: '株式会社〇〇', value: '123'}, ...]
 *
 * @example
 * // Autocomplete用
 * const companyList = useCompanyList(companiesData, { format: 'autocomplete' });
 * // => [{id: 123, value: '株式会社〇〇'}, ...]
 */

// オーバーロード：Select用
export function useCompanyList(
  companiesData: ResultGetGroupedSupportCompanies | undefined,
  options: {format: 'select'},
): SelectOption[];

// オーバーロード：Autocomplete用
export function useCompanyList(
  companiesData: ResultGetGroupedSupportCompanies | undefined,
  options: {format: 'autocomplete'},
): AutocompleteOption[];

// 実装
export function useCompanyList(
  companiesData: ResultGetGroupedSupportCompanies | undefined,
  options: {format: 'select' | 'autocomplete'},
): SelectOption[] | AutocompleteOption[] {
  return useMemo(() => {
    if (!companiesData?.ok) {
      return [];
    }

    const companiesFromApi = companiesData.value.companyGroups.flatMap(
      (group) => group.companies,
    );

    // 重複を除去（Mapのコンストラクタを使用する簡潔な方法）
    const uniqueCompaniesMap = new Map(
      companiesFromApi.map((company) => [company.id, company]),
    );

    const uniqueCompanies = Array.from(uniqueCompaniesMap.values());

    // フォーマットに応じて変換
    if (options.format === 'select') {
      return uniqueCompanies.map((company) => ({
        label: company.name,
        value: company.id.toString(),
      }));
    }

    // autocomplete
    return uniqueCompanies.map((company) => ({
      id: company.id,
      value: company.name,
    }));
  }, [companiesData, options.format]);
}
